import { Client, ConsentState, IdentifierKind, LogLevel, Opfs } from '@xmtp/browser-sdk';
import type { HorusSigner } from '../signers/types';
import { backupXmtpDatabase, restoreXmtpDatabase, ensureFirebaseBackup, hasFirebaseBackup, getPathId } from './xmtpBackup';
import { trySilentFirebaseAuth, authenticateWithExistingSignature, ensureFirebaseAuth, uploadEncryptedFile, downloadEncryptedFile, getFirebaseUid } from './firebaseStorage';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getApp } from 'firebase/app';

const XMTP_ENV = process.env.REACT_APP_NETWORK_TYPE === 'TESTNET' ? 'dev' : 'production';

/**
 * Custom error thrown when the XMTP installation limit (10/10) is reached.
 * Carries the inboxId (and optionally a client) so the UI can show a management modal.
 * When Client.create() itself fails, no client is available — only the inboxId
 * (extracted from the error message) and static SDK methods can be used.
 */
export class InstallationLimitError extends Error {
  client: Client | null;
  inboxId: string;
  constructor(inboxId: string, client: Client | null = null) {
    super('Installation limit reached');
    this.name = 'InstallationLimitError';
    this.inboxId = inboxId;
    this.client = client;
  }
}

/** Extract the InboxID from an XMTP installation limit error message. */
function extractInboxId(err: any): string {
  const match = err?.message?.match(/InboxID\s+([a-f0-9]+)/i);
  return match?.[1] || '';
}

export { XMTP_ENV };
const TAB_LOCK_KEY = 'horus-xmtp-tab-active';
const TAB_ID_KEY = 'horus-xmtp-tab-id';
const DB_KEY_PREFIX = 'horus-xmtp-dbkey-';
const BROADCAST_CHANNEL_NAME = 'horus-xmtp-tab';

function getTabId(): string {
  let id = sessionStorage.getItem(TAB_ID_KEY);
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(TAB_ID_KEY, id);
  }
  return id;
}

/**
 * Get or create a persistent 32-byte encryption key for the XMTP local DB.
 * Stored in localStorage keyed by wallet address.
 */
function getExistingDbKey(address: string): Uint8Array | null {
  const storageKey = DB_KEY_PREFIX + address.toLowerCase();
  const existing = localStorage.getItem(storageKey);
  if (existing) {
    try {
      const arr = JSON.parse(existing);
      if (Array.isArray(arr) && arr.length === 32) {
        return new Uint8Array(arr);
      }
    } catch {
      // Corrupted
    }
  }
  return null;
}

function getOrCreateDbKey(address: string): Uint8Array {
  const existing = getExistingDbKey(address);
  if (existing) return existing;
  const key = crypto.getRandomValues(new Uint8Array(32));
  const storageKey = DB_KEY_PREFIX + address.toLowerCase();
  try {
    localStorage.setItem(storageKey, JSON.stringify(Array.from(key)));
  } catch (err) {
    console.error('[XMTP] Failed to persist DB encryption key to localStorage (storage full?):', err);
    // Key is still usable for this session but won't survive a page reload.
    // The caller should back it up to Firebase as a secondary store.
  }
  return key;
}

// ---- Firebase DB key persistence ----
// Store the XMTP DB encryption key independently in Firebase so it survives
// localStorage clears, browser reinstalls, and device switches.

/**
 * Store the DB encryption key in Firebase Storage.
 * Path: xmtp-backups/{uid}/dbkey.bin (alongside backup.enc and key.bin).
 */
async function storeDbKeyInFirebase(address: string, key: Uint8Array): Promise<void> {
  try {
    const pathId = getPathId(address);
    const path = `xmtp-backups/${pathId}/dbkey.bin`;
    await uploadEncryptedFile(path, key);
    console.info('[XMTP DbKey] Stored DB key in Firebase.');
  } catch (err: any) {
    console.warn('[XMTP DbKey] Failed to store DB key in Firebase:', err?.message);
  }
}

/**
 * Fetch the DB encryption key from Firebase Storage.
 * Tries both UID-based and address-based paths for compatibility.
 * Returns null if no key is stored.
 */
async function fetchDbKeyFromFirebase(address: string): Promise<Uint8Array | null> {
  try {
    const storage = getStorage(getApp());
    const addr = address.toLowerCase();
    const uid = getFirebaseUid();
    const paths: string[] = [];

    if (uid) {
      paths.push(`xmtp-backups/${uid}/dbkey.bin`);
      const normalized = /^0x[0-9a-fA-F]+$/.test(uid) ? uid.toLowerCase() : uid;
      if (normalized !== uid) paths.push(`xmtp-backups/${normalized}/dbkey.bin`);
    }
    paths.push(`xmtp-backups/${addr}/dbkey.bin`);

    for (const path of paths) {
      try {
        const storageRef = ref(storage, path);
        const url = await getDownloadURL(storageRef);
        const data = await downloadEncryptedFile(url);
        if (data.length === 32) {
          // Cache in localStorage for fast future access
          localStorage.setItem(
            DB_KEY_PREFIX + addr,
            JSON.stringify(Array.from(data)),
          );
          console.info('[XMTP DbKey] Restored DB key from Firebase:', path);
          return data;
        }
      } catch {
        // Try next path
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Ensure the DB encryption key is available locally.
 * Checks localStorage first, then tries Firebase.
 * Returns the key if found, null if not available anywhere.
 * Must be called after Firebase auth is established.
 */
export async function ensureDbKeyFromFirebase(address: string): Promise<Uint8Array | null> {
  const existing = getExistingDbKey(address);
  if (existing) return existing;
  return fetchDbKeyFromFirebase(address);
}

/**
 * Wraps a signer to intercept the first signMessage call (from the XMTP SDK)
 * and reuse that signature for Firebase authentication — eliminating the need
 * for a separate Firebase auth popup. The backup key is no longer derived from
 * the signature; it's stored in Firebase Storage instead.
 *
 * Returns the wrapped signer and a promise that resolves when Firebase auth completes.
 */
function createAuthCaptureSigner(
  baseSigner: HorusSigner,
  address: string,
): { signer: HorusSigner; firebaseAuthReady: Promise<void> } {
  let resolveAuth: () => void;
  let rejectAuth: (err: Error) => void;
  const firebaseAuthReady = new Promise<void>((resolve, reject) => {
    resolveAuth = resolve;
    rejectAuth = reject;
  });

  let captured = false;

  const wrappedSigner: HorusSigner = {
    type: baseSigner.type,
    getIdentifier: () => baseSigner.getIdentifier(),
    signMessage: async (message: string): Promise<Uint8Array> => {
      const result = await baseSigner.signMessage(message);
      if (!captured) {
        captured = true;
        // Reuse this signature for Firebase auth (async, non-blocking)
        const hexSig = '0x' + Array.from(result).map(b => b.toString(16).padStart(2, '0')).join('');
        authenticateWithExistingSignature(address, message, hexSig)
          .then(resolveAuth!)
          .catch(rejectAuth!);
      }
      return result;
    },
    signMessageHex: baseSigner.signMessageHex
      ? async (message: string): Promise<string> => {
          const result = await baseSigner.signMessageHex!(message);
          if (!captured) {
            captured = true;
            authenticateWithExistingSignature(address, message, result)
              .then(resolveAuth!)
              .catch(rejectAuth!);
          }
          return result;
        }
      : undefined,
  };

  return { signer: wrappedSigner, firebaseAuthReady };
}

/**
 * Create a new XMTP client. Called on first-time setup or when no local DB exists.
 * This will prompt wallet signatures for identity registration.
 */
export async function createXmtpClient(
  signer: HorusSigner,
  address: string,
  onProgress?: (msg: string) => void
): Promise<Client> {
  onProgress?.('Creating encryption keys...');
  // If no existing key, try Firebase before generating a new one
  if (!getExistingDbKey(address)) {
    // Try to retrieve from Firebase (may have been stored from another device)
    const firebaseKey = await ensureDbKeyFromFirebase(address).catch(() => null);
    if (!firebaseKey) {
      await clearXmtpStorage(address);
    }
  }
  const dbEncryptionKey = getOrCreateDbKey(address);

  let client: Client;
  try {
    // Disable auto-register so we can handle the 10/10 installations limit ourselves.
    client = await Client.create(signer, {
      env: XMTP_ENV as any,
      dbEncryptionKey,
      disableAutoRegister: true,
      loggingLevel: LogLevel.Error,
    });
  } catch (err: any) {
    // The WASM layer may throw the 10/10 error during Client.create() itself.
    // Extract the inboxId from the error message and optionally build a cached client.
    if (isInstallationLimitMsg(err)) {
      const inboxId = extractInboxId(err);
      const builtClient = await buildXmtpClient(address).catch(() => null);
      throw new InstallationLimitError(inboxId, builtClient);
    }
    throw err;
  }

  // Register the identity on the XMTP network
  if (!(await client.isRegistered())) {
    onProgress?.('Registering identity on XMTP network...');
    try {
      await client.register();
    } catch (err: any) {
      // XMTP limits each identity to 10 installations (devices/browsers).
      // If the limit is reached, surface the error so the UI can show
      // a management modal for the user to choose which installations to revoke.
      if (isInstallationLimitMsg(err)) {
        const inboxId = extractInboxId(err) || client.inboxId || '';
        throw new InstallationLimitError(inboxId, client);
      } else {
        throw err;
      }
    }
  }

  // Sync conversations from the network immediately after creating a fresh client.
  // This is critical for new devices — ensures existing conversations are pulled
  // into the local DB before the first conversations.list() call.
  // Use aggressive sync with multiple rounds since the network needs time to
  // propagate the new installation to existing MLS groups.
  await syncConversationsAggressively(client, onProgress, true);

  // First backup: persist the OPFS database to Firebase Storage
  // so it can be restored on a new device or after key rotation.
  // Retry up to 3 times — this backup is critical to avoid creating
  // new installations on every device (10/10 limit).
  onProgress?.('Backing up encryption keys...');
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      await backupXmtpDatabase(address, signer);
      break;
    } catch {
      if (attempt < 3) await new Promise((r) => setTimeout(r, 2000));
    }
  }

  // Verify backup was created
  const backupOk = await hasFirebaseBackup(address).catch(() => false);
  if (!backupOk) {
    console.error('[XMTP Init] WARNING: Backup creation failed — conversations may be lost on cache clear.');
  }

  // Store DB key independently in Firebase for cross-device retrieval
  storeDbKeyInFirebase(address, dbEncryptionKey).catch(() => {});

  return client;
}

/** Check if an error message matches the XMTP 10/10 installation limit pattern. */
function isInstallationLimitMsg(err: any): boolean {
  const msg = err?.message || '';
  return msg.includes('10/10') || (msg.includes('installations') && msg.includes('register'));
}

/**
 * Complete registration after the user has revoked installations via the modal.
 * Retries register() with propagation delays, then aggressively syncs conversations.
 */
export async function registerAfterRevocation(
  client: Client,
  onProgress?: (msg: string) => void,
  signer?: HorusSigner,
  address?: string,
): Promise<Client> {
  onProgress?.('Waiting for network update...');
  await new Promise((r) => setTimeout(r, 3000));

  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      onProgress?.('Registering identity on XMTP network...');
      await client.register();
      break;
    } catch (retryErr: any) {
      if (attempt < 3) {
        await new Promise((r) => setTimeout(r, 3000));
      } else {
        throw retryErr;
      }
    }
  }

  // After re-registration the network needs time to propagate the new installation
  // to existing MLS groups. Use multiple sync rounds with delays.
  await syncConversationsAggressively(client, onProgress, true);

  // Backup after re-registration — retry to ensure Firebase has the new installation
  if (signer && address) {
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await backupXmtpDatabase(address, signer);
        break;
      } catch {
        if (attempt < 3) await new Promise((r) => setTimeout(r, 2000));
      }
    }
  }

  return client;
}

/**
 * Aggressively sync conversations with multiple rounds.
 * After a new installation is registered, existing conversations may not be
 * immediately available because the network needs to propagate welcome messages.
 * This does multiple sync() + syncAll() rounds with delays to maximise retrieval.
 *
 * @param isNewRegistration - When true, sends a sync request to other installations
 *   and uses more rounds with longer delays (total ~30s) since the network needs
 *   time to propagate welcome messages to the new installation.
 */
async function syncConversationsAggressively(
  client: Client,
  onProgress?: (msg: string) => void,
  isNewRegistration?: boolean,
): Promise<number> {
  // For new registrations or restores, request history sync from other installations.
  // This tells existing installations to share their conversation history —
  // critical after restoring from backup where key packages may be stale.
  if (isNewRegistration) {
    try {
      await client.sendSyncRequest();
      console.info('[XMTP Sync] Sent sync request to other installations.');
    } catch (err: any) {
      console.warn('[XMTP Sync] sendSyncRequest failed:', err?.message);
    }
  }

  const rounds = isNewRegistration ? 5 : 3;
  const delays = isNewRegistration ? [2000, 3000, 5000, 8000] : [1000, 2000];
  let prevCount = 0;
  let stableRounds = 0;

  for (let round = 1; round <= rounds; round++) {
    onProgress?.(`Syncing conversations from network (${round}/${rounds})...`);
    // Two-phase sync:
    // 1. conversations.sync() — fetches new welcome messages and conversations from network.
    //    May log WASM "Key Package Reference not found" for stale welcome messages
    //    (expected after backup restore) — these are non-fatal and don't throw.
    // 2. conversations.syncAll() — processes welcomes and syncs messages for unknown + allowed
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await client.conversations.sync();
        break;
      } catch {
        if (attempt < 3) await new Promise((r) => setTimeout(r, 1000));
      }
    }
    // syncAll separately — it may throw on corrupted groups but sync() above already
    // fetched the welcome messages. Don't let syncAll failure block the round.
    try {
      await client.conversations.syncAll([ConsentState.Unknown, ConsentState.Allowed]);
    } catch (syncAllErr: any) {
      console.warn(`[XMTP Sync] syncAll failed in round ${round}:`, syncAllErr?.message);
    }

    // Check if new conversations appeared
    try {
      const convos = await client.conversations.list({
        consentStates: [ConsentState.Unknown, ConsentState.Allowed],
      });
      const count = convos.length;
      console.info(`[XMTP Sync] Round ${round}/${rounds}: ${count} conversations found.`);
      if (count > 0 && count === prevCount) {
        stableRounds++;
        // Break early only if stable for 2 consecutive rounds
        if (stableRounds >= 2) return count;
      } else {
        stableRounds = 0;
      }
      prevCount = count;
    } catch { /* non-critical */ }

    // Re-send sync request midway — other installations may have come online
    if (isNewRegistration && round === 3) {
      try {
        await client.sendSyncRequest();
        console.info('[XMTP Sync] Re-sent sync request midway.');
      } catch { /* non-critical */ }
    }

    if (round < rounds) {
      await new Promise((r) => setTimeout(r, delays[round - 1] || 2000));
    }
  }
  return prevCount;
}

/**
 * Build (reconnect) an existing XMTP client from the local OPFS database.
 * No wallet signatures needed - uses cached identity.
 */
export async function buildXmtpClient(
  address: string,
  onProgress?: (msg: string) => void
): Promise<Client | null> {
  // Only attempt build if we have a stored encryption key from a previous session.
  // If no key exists, the old DB was created without encryption and is incompatible.
  const dbEncryptionKey = getExistingDbKey(address);
  if (!dbEncryptionKey) {
    await clearXmtpStorage(address);
    return null;
  }

  const identifier = {
    identifier: address.toLowerCase(),
    identifierKind: IdentifierKind.Ethereum,
  };

  // Retry with exponential backoff — OPFS file handles from a previous page load
  // may not be released immediately (NoModificationAllowedError). Waiting lets
  // the old WASM instance be garbage collected and handles freed.
  const retryDelays = [1000, 2000, 3000, 5000];
  for (let attempt = 0; attempt <= retryDelays.length; attempt++) {
    try {
      if (attempt > 0) {
        onProgress?.(`Reconnecting... attempt ${attempt + 1}/${retryDelays.length + 1}`);
      }
      const client = await Client.build(identifier, {
        env: XMTP_ENV as any,
        dbEncryptionKey,
        loggingLevel: LogLevel.Error,
      });
      return client;
    } catch (err: any) {
      const msg = err?.message || err?.name || '';
      const isOpfsLock = msg.includes('NoModificationAllowedError') || msg.includes('Access Handle');

      if (isOpfsLock && attempt < retryDelays.length) {
        console.warn(`[XMTP Build] OPFS lock contention, retry ${attempt + 1}/${retryDelays.length} in ${retryDelays[attempt]}ms...`);
        await new Promise((r) => setTimeout(r, retryDelays[attempt]));
        continue;
      }

      if (isOpfsLock) {
        // All retries exhausted but this is a lock error, NOT data corruption.
        // Do NOT clear OPFS — the data is fine, just temporarily inaccessible.
        console.error('[XMTP Build] OPFS lock persists after retries — preserving data for next attempt.');
        return null;
      }

      // Non-lock error (genuine corruption) — clear OPFS and session flag.
      console.error('[XMTP Build] Non-lock error, clearing OPFS:', msg);
      await clearXmtpStorage(address);
      localStorage.removeItem('horus-xmtp-session-ok-' + address.toLowerCase());
      return null;
    }
  }
  return null;
}

async function clearXmtpStorage(_address: string): Promise<void> {
  try {
    // Use SDK's Opfs class — XMTP v6 uses inbox IDs for file naming,
    // so the old address-based filter never matched anything.
    const opfs = await Opfs.create();
    await opfs.clearAll();
    opfs.close();
  } catch {
    // Fallback: clear ALL OPFS root entries (OPFS is per-origin, all ours)
    try {
      const root = await navigator.storage?.getDirectory?.();
      if (root) {
        for await (const [name] of (root as any).entries()) {
          await root.removeEntry(name, { recursive: true }).catch(() => {});
        }
      }
    } catch { }
  }
}

/**
 * Initialize XMTP client — designed for minimal wallet popups:
 *   1. Try local data first (fast, no network, 0 popups)
 *   2. If Firebase auth is silently available (from persistence),
 *      restore from backup (0 popups)
 *   3. Create client with auth-capture signer (1 popup total):
 *      - The SDK's signMessage call is intercepted for Firebase auth
 *      - After Firebase auth, check for backup before registering
 *      - If backup exists: close unregistered client → restore → rebuild
 *      - If no backup: register new client → backup to Firebase
 *
 * This ensures all devices share the same installation identity,
 * minimizing unnecessary installation slot consumption (max 10 per inbox).
 */
export interface InitResult {
  client: Client;
  isNewRegistration: boolean;
}

export async function initializeXmtpClient(
  signer: HorusSigner,
  address: string,
  onProgress?: (msg: string) => void,
  options?: { allowCreateNew?: boolean }
): Promise<InitResult> {
  const allowCreateNew = options?.allowCreateNew !== false;
  // Request persistent storage to prevent mobile browsers from evicting OPFS data.
  // Store the result so backup frequency can be adjusted dynamically.
  try {
    if (navigator.storage?.persist) {
      const persisted = await navigator.storage.persist();
      localStorage.setItem('horus-xmtp-storage-persisted', persisted ? '1' : '0');
      if (!persisted) {
        console.warn('[XMTP Init] Browser denied persistent storage — OPFS may be evicted. Backups will run more frequently.');
      }
    }
  } catch { /* non-critical */ }

  // ---- Step 1: Try local data first (fastest path, 0 popups) ----
  console.info('[XMTP Init] Step 1: Attempting local build for', address);
  const existingClient = await buildXmtpClient(address, onProgress);
  if (existingClient) {
    try {
      onProgress?.('Verifying registration...');
        if (await existingClient.isRegistered()) {
        // Local works — establish Firebase auth for attachments/voice, then backup
        ensureFirebaseAuth(address, signer)
          .then(() => {
            ensureFirebaseBackup(address, signer).catch(() => {});
            // Ensure DB key is stored in Firebase for cross-device retrieval
            const dbKey = getExistingDbKey(address);
            if (dbKey) storeDbKeyInFirebase(address, dbKey).catch(() => {});
          })
          .catch(() => {});
        return { client: existingClient, isNewRegistration: false };
      }
    } catch {
      // Network/transient error — trust the local data
      ensureFirebaseAuth(address, signer)
        .then(() => {
          ensureFirebaseBackup(address, signer).catch(() => {});
          const dbKey = getExistingDbKey(address);
          if (dbKey) storeDbKeyInFirebase(address, dbKey).catch(() => {});
        })
        .catch(() => {});
      return { client: existingClient, isNewRegistration: false };
    }
    try { existingClient.close(); } catch { /* ignore */ }
  }

  // ---- Step 2: Try Firebase restore (only if auth is silently available — 0 popups) ----
  console.info('[XMTP Init] Step 2: Checking for silent Firebase auth...');
  const silentlyAuthed = await trySilentFirebaseAuth();
  if (silentlyAuthed) {
    console.info('[XMTP Init] Silent Firebase auth available, attempting restore...');
    // Before full restore, try retrieving just the DB key from Firebase.
    // If buildXmtpClient failed in Step 1 because the key was missing from
    // localStorage, the DB key in Firebase lets us rebuild without a full restore.
    const firebaseDbKey = await ensureDbKeyFromFirebase(address).catch(() => null);
    if (firebaseDbKey && !existingClient) {
      console.info('[XMTP Init] DB key retrieved from Firebase, retrying local build...');
      const rebuiltClient = await buildXmtpClient(address, onProgress);
      if (rebuiltClient) {
        try {
          if (await rebuiltClient.isRegistered()) {
            ensureFirebaseBackup(address, signer).catch(() => {});
            return { client: rebuiltClient, isNewRegistration: false };
          }
        } catch {
          // Trust restored data on transient error
          return { client: rebuiltClient, isNewRegistration: false };
        }
        try { rebuiltClient.close(); } catch {}
      }
    }

    const restoredClient = await tryRestoreFromBackup(signer, address, onProgress);
    if (restoredClient) {
      // Treat restore as needing recovery sync — the backup may be stale and
      // the 3-minute recovery loop in ChatContext needs to run to pull
      // conversations whose welcome messages had expired key packages.
      return { client: restoredClient, isNewRegistration: true };
    }
  } else {
    console.info('[XMTP Init] No silent Firebase auth — skipping restore (will check after SDK auth).');
  }

  // ---- Step 3: Create client with auth-capture signer (1 popup) ----
  // The SDK's signMessage call is intercepted to also authenticate with Firebase.
  // After Firebase auth, we check for a backup before registering the new identity.
  console.info('[XMTP Init] Step 3: Creating XMTP client with auth-capture signer...');
  if (!allowCreateNew) {
    throw new Error('No local or backup data. Skipping new XMTP identity creation.');
  }

  onProgress?.('Creating new XMTP identity...');
  const { signer: captureSigner, firebaseAuthReady } = createAuthCaptureSigner(signer, address);

  // Always clear stale OPFS data before Client.create() so the SDK performs
  // a fresh identity creation and WILL call signMessage (needed for auth capture).
  // If we reach step 3, local data is known-unusable (step 1 failed or was unregistered).
  await clearXmtpStorage(address);
  // Preserve existing key if available (localStorage or Firebase) to avoid
  // creating duplicate keys that consume installation slots.
  const existingStep3Key = getExistingDbKey(address);
  if (!existingStep3Key) {
    localStorage.removeItem(DB_KEY_PREFIX + address.toLowerCase());
  }
  const dbEncryptionKey = getOrCreateDbKey(address);

  let client: Client;
  try {
    client = await Client.create(captureSigner, {
      env: XMTP_ENV as any,
      dbEncryptionKey,
      disableAutoRegister: true,
      loggingLevel: LogLevel.Error,
    });
  } catch (err: any) {
    if (isInstallationLimitMsg(err)) {
      const inboxId = extractInboxId(err);
      const builtClient = await buildXmtpClient(address).catch(() => null);
      throw new InstallationLimitError(inboxId, builtClient);
    }
    throw err;
  }

  // Wait for Firebase auth from the intercepted SDK signature (with timeout safety net)
  let firebaseAuthed = false;
  try {
    await Promise.race([
      firebaseAuthReady,
      new Promise<void>((_, reject) =>
        setTimeout(() => reject(new Error('Firebase auth capture timeout')), 15000)
      ),
    ]);
    firebaseAuthed = true;
    console.info('[XMTP Init] Firebase auth succeeded via intercepted SDK signature.');
  } catch (authErr: any) {
    console.warn('[XMTP Init] Firebase auth from SDK signature failed or timed out:', authErr?.message);
  }

  // Check for backup now that Firebase is authenticated.
  // Only close + clear + restore if a backup actually exists.
  if (firebaseAuthed) {
    try {
      onProgress?.('Checking for message backup...');
      const backupExists = await hasFirebaseBackup(address);

      if (backupExists) {
        console.info('[XMTP Init] Backup found — closing unregistered client to restore...');
        // Close the unregistered client to release OPFS locks before restore.
        // importOpfsFiles() has its own retry logic for OPFS lock contention.
        try { client.close(); } catch { /* ignore */ }
        await clearXmtpStorage(address);
        // Keep the DB key from Step 3 — don't regenerate to avoid duplicate installations.
        // The restore will overwrite it with the backup's key if restore succeeds.

        const restoredClient = await tryRestoreFromBackup(signer, address, onProgress);
        if (restoredClient) return { client: restoredClient, isNewRegistration: true };

        // Restore failed — re-create the client for registration.
        // Reuse the same dbEncryptionKey from Step 3 to avoid creating yet another key.
        console.warn('[XMTP Init] Backup found but restore failed, re-creating client...');
        onProgress?.('Creating new XMTP identity...');
        client = await Client.create(signer, {
          env: XMTP_ENV as any,
          dbEncryptionKey,
          disableAutoRegister: true,
          loggingLevel: LogLevel.Error,
        });
      } else {
        console.info('[XMTP Init] No backup found — proceeding with new identity.');
      }
    } catch (err: any) {
      if (err?.name === 'InstallationLimitError') throw err;
      // Backup check failed — proceed with registration of the current client
      console.warn('[XMTP Init] Post-auth backup check failed:', err?.message);
    }
  }

  // Register the new identity on the XMTP network
  if (!(await client.isRegistered())) {
    onProgress?.('Registering identity on XMTP network...');
    console.info('[XMTP Init] Registering new installation for inbox:', client.inboxId);
    try {
      await client.register();
      console.info('[XMTP Init] Registration successful. Syncing conversations...');
    } catch (err: any) {
      if (isInstallationLimitMsg(err)) {
        const inboxId = extractInboxId(err) || client.inboxId || '';
        throw new InstallationLimitError(inboxId, client);
      }
      throw err;
    }
  }

  const syncCount = await syncConversationsAggressively(client, onProgress, true);
  console.info('[XMTP Init] Post-registration sync complete:', syncCount, 'conversations found.');

  // Backup the new client to Firebase
  onProgress?.('Backing up encryption keys...');
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      await backupXmtpDatabase(address, signer);
      console.info('[XMTP Init] Backup to Firebase successful.');
      break;
    } catch {
      if (attempt < 3) await new Promise((r) => setTimeout(r, 2000));
    }
  }

  // Verify backup was created — warn loudly if it failed
  const backupOk = await hasFirebaseBackup(address).catch(() => false);
  if (!backupOk) {
    console.error('[XMTP Init] WARNING: Backup creation failed — conversations may be lost on cache clear.');
  }

  // Store DB key independently in Firebase for cross-device retrieval
  const step3DbKey = getExistingDbKey(address);
  if (step3DbKey) storeDbKeyInFirebase(address, step3DbKey).catch(() => {});

  return { client, isNewRegistration: true };
}

/**
 * Attempt to restore from Firebase backup and build a client.
 * Returns the restored client on success, null if no backup or restore failed.
 * Throws InstallationLimitError if registration hits the 10/10 limit.
 */
async function tryRestoreFromBackup(
  signer: HorusSigner,
  address: string,
  onProgress?: (msg: string) => void,
): Promise<Client | null> {
  onProgress?.('Checking for message backup...');

  let restored = false;
  let lastRestoreError: Error | null = null;

  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      restored = await restoreXmtpDatabase(address, signer);
      lastRestoreError = null;
      break;
    } catch (err: any) {
      lastRestoreError = err;
      console.warn(`[XMTP Init] Restore attempt ${attempt}/3 failed:`, err?.message || err);
      if (attempt < 3) {
        await new Promise((r) => setTimeout(r, 2000 * attempt));
      }
    }
  }

  if (lastRestoreError) {
    console.error('[XMTP Init] All restore attempts failed:', lastRestoreError.message);
  }

  if (!restored) return null;

  // After successful restore, store DB key in Firebase for cross-device durability.
  // The restore writes the dbKey to localStorage — persist it to Firebase too.
  const restoredDbKey = getExistingDbKey(address);
  if (restoredDbKey) storeDbKeyInFirebase(address, restoredDbKey).catch(() => {});

  // Signal ChatContext to run an immediate archive merge after restore,
  // filling in messages from the gap between backup time and now.
  localStorage.setItem('horus-xmtp-just-restored', '1');

  onProgress?.('Restored messages from backup!');
  const restoredClient = await buildXmtpClient(address, onProgress);
  if (!restoredClient) return null;

  try {
    onProgress?.('Verifying registration...');
    if (await restoredClient.isRegistered()) {
      // Treat restore like a new registration for sync purposes:
      // - Sends sendSyncRequest() so other installations share conversation history
      // - Uses more sync rounds with longer delays to recover conversations
      //   whose welcome messages had stale key packages in the backup
      await syncConversationsAggressively(restoredClient, onProgress, true);
      // Immediately back up the freshly synced state so Firebase has the latest
      backupXmtpDatabase(address, signer).catch(() => {});
      return restoredClient;
    }

    // Restored but not registered (may have been revoked) — try re-registration
    onProgress?.('Re-registering restored identity...');
    try {
      await restoredClient.register();
      await syncConversationsAggressively(restoredClient, onProgress, true);
      backupXmtpDatabase(address, signer).catch(() => {});
      return restoredClient;
    } catch (regErr: any) {
      if (isInstallationLimitMsg(regErr)) {
        const inboxId = extractInboxId(regErr) || restoredClient.inboxId || '';
        throw new InstallationLimitError(inboxId, restoredClient);
      }
    }
  } catch (err: any) {
    if (err?.name === 'InstallationLimitError') throw err;
    // Network/transient error — trust the restored data
    return restoredClient;
  }

  try { restoredClient.close(); } catch { /* ignore */ }
  return null;
}

/**
 * Force-reset the XMTP client by clearing all local state (OPFS database + db key).
 * This is needed when the local identity data becomes incompatible with the current
 * SDK version (e.g. after upgrading from v5.1.0 to v5.3.0). The next initialization
 * will create a fresh client with new installation keys.
 */
export async function forceResetXmtpClient(client: Client | null, address: string): Promise<void> {
  // Close the existing client
  if (client) {
    try { client.close(); } catch { /* ignore */ }
  }
  releaseTabLock();

  // Clear OPFS database
  await clearXmtpStorage(address);

  // Clear db encryption key and backup encryption key cache so
  // createXmtpClient generates fresh ones and the old corrupted
  // backup cannot be restored.
  const addr = address.toLowerCase();
  localStorage.removeItem(DB_KEY_PREFIX + addr);
  localStorage.removeItem('horus-xmtp-bkkey-' + addr);
}

/**
 * Close and cleanup XMTP client.
 */
export async function closeXmtpClient(client: Client | null): Promise<void> {
  if (!client) return;
  try {
    client.close();
    releaseTabLock();
  } catch {
    // Closing errors are non-critical
  }
}

function parseLock(raw: string): { tabId: string | null; timestamp: number } {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && typeof parsed.tabId === 'string') {
      return { tabId: parsed.tabId, timestamp: parsed.timestamp || 0 };
    }
  } catch {
    // not JSON
  }
  // Old format (plain number string) or corrupt — no tab ownership info
  const ts = parseInt(raw, 10);
  return { tabId: null, timestamp: isNaN(ts) ? 0 : ts };
}

/**
 * Check if another tab already has an active XMTP session.
 * OPFS SQLite doesn't support multi-tab access.
 */
export function acquireTabLock(): boolean {
  const myTabId = getTabId();

  // If this tab already owns the lock via interval, allow re-entry
  if ((window as any).__xmtpTabLockInterval) {
    return true;
  }

  const existing = localStorage.getItem(TAB_LOCK_KEY);
  if (existing) {
    const lock = parseLock(existing);
    // Old-format lock (no tabId) — always take it over since we can't
    // tell if it's ours or another tab's, and no interval is refreshing it
    if (lock.tabId !== null) {
      const isOwnLock = lock.tabId === myTabId;
      const isStale = Date.now() - lock.timestamp > 30000;
      // Block only if a different tab holds a fresh lock
      if (!isOwnLock && !isStale) {
        return false;
      }
    }
  }

  const lockValue = JSON.stringify({ tabId: myTabId, timestamp: Date.now() });
  localStorage.setItem(TAB_LOCK_KEY, lockValue);
  // Keep refreshing the lock
  const interval = setInterval(() => {
    localStorage.setItem(TAB_LOCK_KEY, JSON.stringify({ tabId: myTabId, timestamp: Date.now() }));
  }, 10000);
  (window as any).__xmtpTabLockInterval = interval;

  // Clean up lock when tab is closed
  window.addEventListener('beforeunload', releaseTabLock);

  return true;
}

export function releaseTabLock(): void {
  localStorage.removeItem(TAB_LOCK_KEY);
  window.removeEventListener('beforeunload', releaseTabLock);
  const interval = (window as any).__xmtpTabLockInterval;
  if (interval) {
    clearInterval(interval);
    delete (window as any).__xmtpTabLockInterval;
  }
}

/**
 * Request takeover from the tab currently holding the lock.
 * Sends a BroadcastChannel message and waits up to 2s for an ack.
 * After this resolves the caller should retry acquireTabLock().
 */
export function requestTabTakeover(): Promise<void> {
  const TAKEOVER_TIMEOUT_MS = 2000;
  const myTabId = getTabId();

  return new Promise((resolve) => {
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    } catch {
      // BroadcastChannel not supported — force-clear lock
      localStorage.removeItem(TAB_LOCK_KEY);
      resolve();
      return;
    }

    let settled = false;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'takeover-ack' && !settled) {
        settled = true;
        channel.removeEventListener('message', onMessage);
        channel.close();
        resolve();
      }
    };

    channel.addEventListener('message', onMessage);
    channel.postMessage({ type: 'takeover-request', tabId: myTabId });

    // If old tab doesn't ack in time, force-clear the lock
    setTimeout(() => {
      if (!settled) {
        settled = true;
        channel.removeEventListener('message', onMessage);
        channel.close();
        localStorage.removeItem(TAB_LOCK_KEY);
        resolve();
      }
    }, TAKEOVER_TIMEOUT_MS);
  });
}

/**
 * Listen for takeover requests from other tabs.
 * When a request arrives, calls onTakeover() then sends an ack.
 * Returns a cleanup function that closes the channel.
 */
export function listenForTakeover(onTakeover: () => void): () => void {
  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  } catch {
    return () => {};
  }

  const myTabId = getTabId();

  const onMessage = (event: MessageEvent) => {
    if (event.data?.type === 'takeover-request') {
      onTakeover();
      try {
        channel.postMessage({ type: 'takeover-ack', tabId: myTabId });
      } catch {
        // Channel may already be closing
      }
    }
  };

  channel.addEventListener('message', onMessage);

  return () => {
    channel.removeEventListener('message', onMessage);
    channel.close();
  };
}

/**
 * Check if an address can receive XMTP messages (has registered on the network).
 */
export async function canMessage(
  client: Client,
  addresses: string[]
): Promise<Map<string, boolean>> {
  const result = new Map<string, boolean>();
  try {
    const identifiers = addresses.map((addr) => ({
      identifier: addr.toLowerCase(),
      identifierKind: IdentifierKind.Ethereum,
    }));
    const canMessageResult = await client.canMessage(identifiers);
    // SDK may key results by Identifier objects, not address strings.
    // Use positional matching from the values for robustness.
    const values = Array.from(canMessageResult.values());
    addresses.forEach((addr, i) => {
      result.set(addr.toLowerCase(), values[i] ?? false);
    });
  } catch {
    addresses.forEach((addr) => result.set(addr.toLowerCase(), false));
  }
  return result;
}
